import React, { useEffect, useState } from 'react';
import { Activity, TrendingUp, TrendingDown, Clock, Plus, Check, Search } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { usePortfolio } from '../hooks/usePortfolio';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

function MostActivePage() {
  const [activeStocks, setActiveStocks] = useState([]);
  const [metric, setMetric] = useState('volume');
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const { togglePortfolio, isInPortfolio } = usePortfolio();

  useEffect(() => {
    fetchActiveStocks();
  }, [metric]);

  const fetchActiveStocks = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/active/latest?metric=${metric}&limit=500`);
      const data = await response.json();
      
      if (data.success) {
        setActiveStocks(data.data);
        if (data.last_updated && data.last_updated.timestamp) {
          setLastUpdated(new Date(data.last_updated.timestamp));
        }
      }
    } catch (err) {
      console.error('Error fetching active stocks:', err);
      setError('Failed to load active stocks');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading active stocks...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="text-center">
          <p className="text-destructive mb-4">{error}</p>
          <button onClick={fetchActiveStocks} className="btn-primary">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      {/* Header */}
      <header className="bg-background border-b border-border px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h1 className="text-2xl font-bold font-display">Most Active Stocks</h1>
              <p className="text-sm text-muted-foreground">Top traded ASX stocks</p>
            </div>
            <div className="text-right">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Clock className="w-3 h-3" />
                {lastUpdated ? (
                  <span data-testid="last-updated">
                    {formatDistanceToNow(lastUpdated, { addSuffix: true })}
                  </span>
                ) : (
                  <span>Loading...</span>
                )}
              </div>
            </div>
          </div>
          
          {/* Metric Selector */}
          <div className="flex gap-2">
            <button
              onClick={() => setMetric('volume')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                metric === 'volume' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-card text-muted-foreground hover:text-foreground'
              }`}
              data-testid="filter-volume"
            >
              By Volume
            </button>
            <button
              onClick={() => setMetric('value')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                metric === 'value' 
                  ? 'bg-primary text-primary-foreground' 
                  : 'bg-card text-muted-foreground hover:text-foreground'
              }`}
              data-testid="filter-value"
            >
              By Value
            </button>
          </div>
        </div>
      </header>

      {/* Stats Cards */}
      <div className="px-4 py-4">
        <div className="max-w-7xl mx-auto grid grid-cols-3 gap-3">
          <div className="stats-card">
            <div className="text-2xl font-bold text-blue-500" data-testid="total-active">
              {activeStocks.length}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Total Stocks</div>
          </div>
          <div className="stats-card">
            <div className="text-2xl font-bold text-green-500">
              {activeStocks.filter(s => s.change_pct > 0).length}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Gainers</div>
          </div>
          <div className="stats-card">
            <div className="text-2xl font-bold text-red-500">
              {activeStocks.filter(s => s.change_pct < 0).length}
            </div>
            <div className="text-xs text-muted-foreground mt-1">Losers</div>
          </div>
        </div>

        {/* Search bar */}
        <div className="max-w-7xl mx-auto mt-3 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search ticker or company…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-card border border-border rounded-lg text-sm focus:outline-none focus:border-emerald-500/50"
            data-testid="active-search"
          />
        </div>
      </div>

      {/* Active Stocks List */}
      <div className="px-4 pb-6">
        <div className="max-w-7xl mx-auto space-y-2">
          {activeStocks
            .filter(stock => {
              if (!search) return true;
              const q = search.toLowerCase();
              return (stock.symbol || '').toLowerCase().includes(q) || (stock.name || '').toLowerCase().includes(q);
            })
            .map((stock, index) => (
            <div
              key={stock.symbol}
              className="stock-card"
              data-testid={`active-stock-${stock.symbol}`}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold text-muted-foreground">#{index + 1}</span>
                    <h3 className="text-lg font-semibold">{stock.symbol}</h3>
                  </div>
                  <p className="text-sm text-muted-foreground mb-2">{stock.name}</p>
                  
                  <div className="flex items-center gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Price:</span>
                      <span className="ml-1 font-semibold">${stock.price.toFixed(2)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Volume:</span>
                      <span className="ml-1 font-semibold">
                        {(stock.volume / 1000000).toFixed(2)}M
                      </span>
                    </div>
                    {stock.value_traded && (
                      <div>
                        <span className="text-muted-foreground">Value:</span>
                        <span className="ml-1 font-semibold text-blue-400">
                          ${(stock.value_traded / 1000000).toFixed(1)}M
                        </span>
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Add to Portfolio Button - Centered */}
                <button
                  onClick={(e) => { e.stopPropagation(); togglePortfolio(stock, 'Active'); }}
                  className={`mx-4 p-3 rounded-full transition-all ${
                    isInPortfolio(stock.symbol)
                      ? 'bg-green-500/30 text-green-400 hover:bg-red-500/30 hover:text-red-400'
                      : 'bg-card-hover hover:bg-primary/20 text-muted-foreground hover:text-primary'
                  }`}
                  data-testid={`add-portfolio-${stock.symbol}`}
                  title={isInPortfolio(stock.symbol) ? 'Remove from Portfolio' : 'Add to Portfolio'}
                >
                  {isInPortfolio(stock.symbol) ? (
                    <Check className="w-5 h-5" />
                  ) : (
                    <Plus className="w-5 h-5" />
                  )}
                </button>
                
                <div className="text-right min-w-[80px]">
                  <div className={`text-2xl font-bold flex items-center justify-end gap-1 ${
                    stock.change_pct >= 0 ? 'text-green-500' : 'text-red-500'
                  }`} data-testid={`change-${stock.symbol}`}>
                    {stock.change_pct >= 0 ? (
                      <TrendingUp className="w-5 h-5" />
                    ) : (
                      <TrendingDown className="w-5 h-5" />
                    )}
                    {Math.abs(stock.change_pct).toFixed(2)}%
                  </div>
                  <div className="text-xs text-muted-foreground mt-1">Change</div>
                </div>
              </div>
            </div>
          ))}
          
          {activeStocks.length === 0 && (
            <div className="text-center py-12">
              <Activity className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No active stocks data available</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MostActivePage;
